import { commonShortFieldLabels } from "./common-short-fields";

export const examAdminAiTasks = {
  pageTitle: "AI Workspace",
  pageSubtitle:
    "Create AI tasks from templates and run the real AI question generation, paper generation, and review flow.",
  drawers: {
    createTitle: "Create AI Task",
    updateTitle: "Update AI Task",
    updateTitleWithId: "Update AI Task #{id}",
  },
  fields: {
    taskType: "Task Type",
    sourceModule: "Source Module",
    sourceRefId: "Source Object ID",
    modelName: "Model Name",
    promptVersion: "Prompt Version",
    inputJson: "input_json Preview",
    taskStatus: "Execution Status",
    reviewStatus: "Review Status",
    resultJson: "result_json",
  },
  actions: {
    create: "Create AI Task",
    runTask: "Run Task",
    applyResult: "Apply Result",
    updateResult: "Update Result",
    saveUpdate: "Save Update",
  },
  messages: {
    createSuccess: "AI task created",
    updateSuccess: "AI task updated",
    runSuccess: "AI task completed, mode: {mode}",
    applySuccess: "AI result applied as {targetType} draft #{targetId}",
    appliedTarget: "Applied as {type} #{id}",
  },
  summary: {
    total: "Total tasks: {count}",
    completed: "Completed: {count}",
    pending: "Pending: {count}",
    failed: "Failed: {count}",
  },
  table: {
    taskType: "Task Type",
    sourceModule: "Source Module",
    taskStatus: "Execution Status",
    reviewStatus: "Review Status",
    model: "Model",
    createdAt: commonShortFieldLabels.createdAt,
    actions: "Actions",
  },
  options: {
    taskTypes: {
      ingest: "AI Ingest",
      generateQuestion: "AI Question Generation",
      generatePaper: "AI Paper Generation",
      scoreSubjective: "AI Review",
      extractKnowledge: "Knowledge Extraction",
    },
    taskStatusPending: "Pending",
    taskStatusRunning: "Running",
    taskStatusCompleted: "Completed",
    taskStatusFailed: "Failed",
    reviewStatusPending: "Pending Review",
    reviewStatusApproved: "Approved",
    reviewStatusRejected: "Rejected",
  },
  editor: {
    required: "This field is required",
    candidateQuestionTitle: "Candidate Question {index}",
    previewHint:
      "The AI workspace maintains input through a templated form and generates input_json automatically.",
    sections: {
      ingestInput: "Ingest Input",
      generateQuestionInput: "Question Generation Input",
      generatePaperInput: "Paper Generation Input",
      scoreInput: "Review Input",
      extractKnowledgeInput: "Knowledge Extraction Input",
    },
    fields: {
      topic: "Topic",
      sourceText: "Source Material",
      constraints: "Constraints",
      availableQuestions: "Candidate Questions",
      questionVersionId: "Question Version ID (Numeric)",
      questionTitle: "Question Title",
      knowledgePoints: "Knowledge Points",
      candidateAnswer: "Candidate Answer",
      fullScore: "Full Score",
      referencePoints: "Reference Scoring Points",
    },
    actions: {
      addConstraint: "Add Constraint",
      addCandidateQuestion: "Add Candidate Question",
      addKnowledgePoint: "Add Knowledge Point",
      addReferencePoint: "Add Scoring Point",
    },
    placeholders: {
      ingestSourceText: "Enter the source material so AI can turn it into a draft question",
      constraint: "Example: Must stay focused on SQL injection",
      knowledgePoint: "Example: SQL Injection",
      referencePoint: "Example: Explains the injection principle",
      sourceModule: "For example question_bank / paper / review",
      modelName: "Leave empty to use the Intelligence Center model connection default",
      promptVersion: "v1",
    },
    templates: {
      topic: {
        sqlInjection: "SQL Injection",
      },
      ingest: {
        title: "SQL Injection Case Reading Question",
        sourceText:
          "A system concatenates the username and password directly into an SQL query during login, allowing attackers to craft input that bypasses authentication. Turn this material into a short-answer question.",
      },
      generateQuestion: {
        constraintSecurityFocused:
          "The question must focus on SQL injection in web security and should not become a generic SQL query question",
        constraintDifferentiate:
          "It should distinguish conceptual understanding from defense strategies",
      },
      generatePaper: {
        question1Title: "Basic SQL Injection Identification",
        question1KnowledgePoint: "Injection Identification",
        question2Title: "SQL Injection Principle Analysis",
        question2KnowledgePoint: "Injection Principles",
      },
      scoreSubjective: {
        questionTitle: "Explain the principle of SQL injection and describe two defense methods",
        questionStem: "Explain the principle of SQL injection and describe two defense methods",
        point1: "User input is concatenated directly into the SQL statement",
        point2: "Attackers can alter the query logic",
        point3: "Parameterized queries",
        point4: "Input validation or least privilege",
        answer:
          "SQL injection means malicious input is concatenated directly into SQL, which changes the original query logic. Defenses include parameterized queries together with input validation.",
      },
      extractKnowledge: {
        stem: "Analyze the differences between SQL injection, boolean-based blind SQL injection, and time-based blind SQL injection, and explain common defense strategies.",
      },
    },
  },
} as const;
